// Crop, black out and point at things in a screenshot before it is attached — "show us exactly
// what you mean, and nothing you don't want us to see". Everything is drawn on a canvas with no
// library, so a blacked-out area is solid pixels in the exported PNG, not a reversible blur.
// Rendered through GfPortal so it covers the whole viewport in the app's real theme (the
// assistant panel clips its children and uses the inverted theme).
import { useEffect, useRef, useState, type PointerEvent } from "react";
import { ArrowUpRight, Crop, Eraser, Maximize, Square, Undo2, X } from "lucide-react";
import { GfPortal } from "@gf/ui";
import "./image-editor.css";

type Tool = "crop" | "redact" | "box" | "arrow";
interface Shape { tool: Tool; x: number; y: number; x2: number; y2: number }
interface Rect { x: number; y: number; w: number; h: number }

const ACCENT = "#ef4444";
const REDACT = "#111827";
const TOOLS: { tool: Tool; label: string; hint: string; Icon: typeof Crop }[] = [
  { tool: "crop", label: "Crop", hint: "Drag to keep just the part that matters.", Icon: Crop },
  { tool: "redact", label: "Black out", hint: "Drag over anything private — it's removed for good.", Icon: Eraser },
  { tool: "box", label: "Box", hint: "Drag to draw a box around the problem.", Icon: Square },
  { tool: "arrow", label: "Arrow", hint: "Drag to point an arrow at it.", Icon: ArrowUpRight },
];

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);
export function rectOf(s: { x: number; y: number; x2: number; y2: number }): Rect {
  return { x: Math.min(s.x, s.x2), y: Math.min(s.y, s.y2), w: Math.abs(s.x2 - s.x), h: Math.abs(s.y2 - s.y) };
}

function drawShape(ctx: CanvasRenderingContext2D, s: Shape, lw: number) {
  if (s.tool === "arrow") {
    ctx.strokeStyle = ACCENT;
    ctx.fillStyle = ACCENT;
    ctx.lineWidth = lw;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(s.x, s.y);
    ctx.lineTo(s.x2, s.y2);
    ctx.stroke();
    const angle = Math.atan2(s.y2 - s.y, s.x2 - s.x), head = lw * 4;
    ctx.beginPath();
    ctx.moveTo(s.x2, s.y2);
    ctx.lineTo(s.x2 - head * Math.cos(angle - Math.PI / 6), s.y2 - head * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(s.x2 - head * Math.cos(angle + Math.PI / 6), s.y2 - head * Math.sin(angle + Math.PI / 6));
    ctx.closePath();
    ctx.fill();
    return;
  }
  const r = rectOf(s);
  if (s.tool === "redact") {
    ctx.fillStyle = REDACT;
    ctx.fillRect(r.x, r.y, r.w, r.h);
  } else {
    ctx.strokeStyle = ACCENT;
    ctx.lineWidth = lw;
    ctx.strokeRect(r.x, r.y, r.w, r.h);
  }
}

export function ImageEditor({ file, onCancel, onDone }: { file: File; onCancel: () => void; onDone: (edited: File) => void }) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [tool, setTool] = useState<Tool>("crop");
  const [crop, setCrop] = useState<Rect | null>(null);
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [draft, setDraft] = useState<Shape | null>(null);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef<Shape | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => setImg(image);
    image.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Escape discards the edit WITHOUT also closing the assistant behind it (its listener is on window).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      e.preventDefault();
      onCancel();
    };
    const onResize = () => setView({ w: window.innerWidth, h: window.innerHeight });
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("resize", onResize);
    };
  }, [onCancel]);

  const natW = img?.naturalWidth ?? 0, natH = img?.naturalHeight ?? 0;
  const lineWidth = Math.max(3, Math.round(natW / 240));
  const fit = natW && natH ? Math.min((Math.min(view.w, 1040) - 56) / natW, (view.h - 200) / natH, 1) : 1;
  const dispW = Math.max(1, Math.round(natW * fit)), dispH = Math.max(1, Math.round(natH * fit));

  useEffect(() => {
    const canvas = canvasRef.current, ctx = canvas?.getContext("2d");
    if (!canvas || !ctx || !img) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(fit, 0, 0, fit, 0, 0);
    ctx.drawImage(img, 0, 0);
    for (const s of shapes) drawShape(ctx, s, lineWidth);
    if (draft && draft.tool !== "crop") drawShape(ctx, draft, lineWidth);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    const box = draft?.tool === "crop" ? rectOf(draft) : crop;
    if (box) {
      const dx = box.x * fit, dy = box.y * fit, dw = box.w * fit, dh = box.h * fit;
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.fillRect(0, 0, canvas.width, dy);
      ctx.fillRect(0, dy + dh, canvas.width, canvas.height - (dy + dh));
      ctx.fillRect(0, dy, dx, dh);
      ctx.fillRect(dx + dw, dy, canvas.width - (dx + dw), dh);
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(dx, dy, dw, dh);
      ctx.setLineDash([]);
    }
  }, [img, shapes, draft, crop, fit, lineWidth, dispW, dispH]);

  function toNatural(e: PointerEvent<HTMLCanvasElement>) {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: clamp(((e.clientX - r.left) / r.width) * natW, 0, natW), y: clamp(((e.clientY - r.top) / r.height) * natH, 0, natH) };
  }
  function down(e: PointerEvent<HTMLCanvasElement>) {
    if (!img || e.button !== 0) return;
    const p = toNatural(e);
    drawing.current = { tool, x: p.x, y: p.y, x2: p.x, y2: p.y };
    setDraft(drawing.current);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* a nicety */ }
  }
  function move(e: PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const p = toNatural(e);
    drawing.current = { ...drawing.current, x2: p.x, y2: p.y };
    setDraft(drawing.current);
  }
  function up() {
    // Read the live drag from the ref: a quick click can end before React re-renders.
    const done = drawing.current;
    drawing.current = null;
    setDraft(null);
    if (!done) return;
    const r = rectOf(done);
    // A stray click that didn't drag out an area does nothing.
    const meaningful = done.tool === "arrow" ? r.w + r.h > 6 : r.w > 4 && r.h > 4;
    if (meaningful) {
      if (done.tool === "crop") setCrop(r);
      else setShapes((prev) => [...prev, done]);
    }
  }

  async function attach() {
    if (!img || saving) return;
    setSaving(true);
    try {
      const region = crop ?? { x: 0, y: 0, w: natW, h: natH };
      const out = document.createElement("canvas");
      out.width = Math.max(1, Math.round(region.w));
      out.height = Math.max(1, Math.round(region.h));
      const ctx = out.getContext("2d");
      if (!ctx) return;
      ctx.translate(-region.x, -region.y);
      ctx.drawImage(img, 0, 0);
      for (const s of shapes) drawShape(ctx, s, lineWidth);
      const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, "image/png"));
      if (blob) onDone(new File([blob], `${file.name.replace(/\.[^.]+$/, "").trim() || "screenshot"}.png`, { type: "image/png" }));
    } finally {
      setSaving(false);
    }
  }

  return (
    <GfPortal>
      <div className="gf-image-editor" role="dialog" aria-modal="true" aria-label="Edit screenshot">
        <div className="gf-image-editor__panel">
          <header>
            <strong>Edit screenshot</strong>
            <button type="button" className="gf-image-editor__icon" onClick={onCancel} aria-label="Discard and go back"><X size={16} /></button>
          </header>
          <div className="gf-image-editor__tools" role="toolbar" aria-label="Editing tools">
            {TOOLS.map(({ tool: t, label, Icon }) => (
              <button key={t} type="button" aria-pressed={tool === t} onClick={() => setTool(t)}><Icon size={14} /> {label}</button>
            ))}
            <span className="gf-image-editor__sep" />
            <button type="button" onClick={() => setShapes((p) => p.slice(0, -1))} disabled={!shapes.length}><Undo2 size={14} /> Undo</button>
            <button type="button" onClick={() => setCrop(null)} disabled={!crop}><Maximize size={14} /> Reset crop</button>
          </div>
          <div className="gf-image-editor__stage">
            {img ? (
              <canvas ref={canvasRef} width={dispW} height={dispH} style={{ aspectRatio: `${natW} / ${natH}` }} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
            ) : (
              <p role="status">Loading the screenshot…</p>
            )}
          </div>
          <footer>
            <span>{TOOLS.find((t) => t.tool === tool)?.hint}</span>
            <button type="button" onClick={onCancel}>Cancel</button>
            <button type="button" className="gf-image-editor__primary" onClick={() => void attach()} disabled={!img || saving}>{saving ? "Saving…" : "Attach"}</button>
          </footer>
        </div>
      </div>
    </GfPortal>
  );
}
